import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { getMetricFormat, type MetricV2Response } from '@data/config/metrics';
import formatDate from '@data/utils/format-date';
import { getEdApps } from '@data/utils/get-ed-apps';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * POST /v2/metrics/sessions — the two session metrics for every edApp at once.
 *
 * The reporting service answers this route from a single query: the entitled
 * student roster already carries `ed_app`, so the per-product split is a GROUP BY
 * rather than a request per product. One call therefore serves both the landing
 * page Highlights (from `total_result`) and every product card (from `edApps`).
 *
 * `useMetricsBatchV2Api` covers any combination of metrics but totals across
 * edApps, which cannot fill a card per product; this route is the per-edApp
 * split of the two session metrics, so the landing page needs this one and not
 * both.
 *
 * The route accepts only TOTAL_STUDENT_SESSIONS and TOTAL_STUDENT_MINUTES —
 * anything else is a 400 — so the metric types are fixed here rather than taken
 * from the caller.
 */

// ── Supported metric types ─────────────────────────────────────────────────────

export const SESSION_METRIC_TYPES = [
  'TOTAL_STUDENT_SESSIONS',
  'TOTAL_STUDENT_MINUTES',
] as const;

export type SessionMetricType = (typeof SESSION_METRIC_TYPES)[number];

// ── Response schema ────────────────────────────────────────────────────────────

const sessionMetricSchema = z.object({
  metricType: z.enum(SESSION_METRIC_TYPES),
  value: z.string().nullable(),
  label: z.string(),
  toolTip: z.string().optional(),
});

const metricsSessionsV2Schema = z.object({
  /** Summed across every edApp in the breakdown below. */
  total_result: z.array(sessionMetricSchema),
  edApps: z.array(
    z.object({
      edapp: z.string(),
      metric: z.array(sessionMetricSchema),
    })
  ),
});

// ── Parsed shape ───────────────────────────────────────────────────────────────

/** The two values a product card renders, for one edApp. */
export interface SessionEdAppMetrics {
  sessions: string | null;
  minutes: string | null;
}

export interface MetricsSessionsV2Response {
  /** Highlight cards — one entry per metric type, in the order requested. */
  totalResult: MetricV2Response[];
  /**
   * The per-edApp breakdown, keyed by upper-cased edApp name. The service echoes
   * back whatever casing the request used (`litpro` in, `litpro` out), while
   * application codes are a mix of upper and lower case, so both sides of the
   * lookup are upper-cased rather than compared as sent.
   */
  edApps: Record<string, SessionEdAppMetrics>;
}

// ── Request body ───────────────────────────────────────────────────────────────

export interface MetricsSessionsV2Request {
  orgId: string;
  teacherIds: string[];
  startDate: Date;
  endDate: Date;
  edApps: string[];
  classIds?: string[] | null;
  studentIds?: string[] | null;
}

export interface UseMetricsSessionsV2ApiProps extends MetricsSessionsV2Request {
  enabled?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

type ParsedSessionMetric = z.infer<typeof sessionMetricSchema>;

function valueOf(
  metrics: ParsedSessionMetric[],
  metricType: SessionMetricType
): string | null {
  return metrics.find((m) => m.metricType === metricType)?.value ?? null;
}

/**
 * The metrics for one product, or undefined when the response carries no bucket
 * for it — a product the teacher holds but that reported no sessions at all.
 *
 * Lookup goes through `getEdApps` so the LITPRO/LP_RMMVP pair resolves to
 * whichever name the service bucketed the rows under.
 */
export function selectEdAppMetrics(
  data: MetricsSessionsV2Response | undefined,
  applicationCode: string
): SessionEdAppMetrics | undefined {
  if (!data) return undefined;

  return getEdApps(applicationCode)
    .map((edApp) => data.edApps[edApp.toUpperCase()])
    .find((metrics): metrics is SessionEdAppMetrics => !!metrics);
}

// ── Fetcher ────────────────────────────────────────────────────────────────────

export const getMetricsSessionsV2Api = async ({
  orgId,
  teacherIds,
  startDate,
  endDate,
  edApps,
  classIds,
  studentIds,
}: MetricsSessionsV2Request): Promise<MetricsSessionsV2Response> => {
  const body = {
    orgId,
    metricTypes: SESSION_METRIC_TYPES,
    teacherIds,
    startDate: formatDate(startDate),
    endDate: formatDate(endDate, { boundary: 'end' }),
    edApps,
    classIds: classIds ?? null,
    studentIds: studentIds ?? null,
  };

  return fetcher('/metrics/sessions', {
    service: '/data',
    version: '/v2',
    method: 'POST',
    body: JSON.stringify(body),
  }).then((res) => {
    const parsed = metricsSessionsV2Schema.parse(res);

    return {
      totalResult: parsed.total_result.map(({ metricType, ...metric }) => ({
        ...metric,
        format: getMetricFormat(metricType),
      })),
      edApps: Object.fromEntries(
        parsed.edApps.map(({ edapp, metric }) => [
          edapp.toUpperCase(),
          {
            sessions: valueOf(metric, 'TOTAL_STUDENT_SESSIONS'),
            minutes: valueOf(metric, 'TOTAL_STUDENT_MINUTES'),
          },
        ])
      ),
    };
  });
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useMetricsSessionsV2Api({
  enabled,
  ...request
}: UseMetricsSessionsV2ApiProps): UseQueryResult<
  MetricsSessionsV2Response,
  Error
> {
  const {
    orgId,
    teacherIds,
    startDate,
    endDate,
    edApps,
    classIds,
    studentIds,
  } = request;

  return useQuery({
    queryKey: [
      'metrics-sessions-v2',
      {
        orgId,
        teacherIds,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate, { boundary: 'end' }),
        edApps,
        classIds: classIds ?? null,
        studentIds: studentIds ?? null,
      },
    ],
    queryFn: () => getMetricsSessionsV2Api(request),
    enabled: enabled !== false,
    staleTime: 3 * 60 * 1000, // 3 Min Stale Time
    refetchOnWindowFocus: false,
  });
}
